import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/roboto/900.css';
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query';
import App from './App.jsx';
import './index.css';
import { createAppTheme } from './theme';
import { SearchProvider } from './context/SearchContext.jsx';
import { DashboardProvider } from './context/DashboardContext.jsx';
import { BulkSendStagingProvider } from './context/BulkSendStagingContext.jsx';
import { ThemeModeProvider, useThemeMode } from './hooks/useThemeMode.jsx';
// PWA service worker registration is handled by the PWA hook (`usePWA`) + vite-plugin-pwa.
// Keep main boot lean to avoid double-registering service workers.

import {
  GoogleDomainAuthProvider,
  GoogleDomainAuthGate,
} from './auth/googleDomainAuth.jsx';
import { PWAProvider } from './context/PWAContext.jsx';
import AppErrorBoundary from './components/AppErrorBoundary.jsx';
import { installGlobalErrorHandlers, attachAxiosLogging, logger } from './utils/logger.js';

installGlobalErrorHandlers();
attachAxiosLogging(axios);
import { getBuildLabel } from './utils/buildInfo.js';

const BUILD_LABEL = getBuildLabel();
logger.info(`Bootstrapping frontend (build ${BUILD_LABEL})`, {
  mode: import.meta.env.MODE,
  authRequired: import.meta.env?.VITE_AUTH_REQUIRED,
});

const safePreview = (value) => {
  if (Array.isArray(value)) return `array(${value.length})`;
  if (value && typeof value === 'object') return Object.keys(value).slice(0, 5);
  return value;
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      logger.error('[RQ] Query error', {
        queryKey: query.queryKey,
        message: error?.message,
        stack: error?.stack,
        state: query.state?.status,
      });
    },
    onSuccess: (data, query) => {
      logger.debug('[RQ] Query success', {
        queryKey: query.queryKey,
        dataPreview: safePreview(data),
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      logger.error('[RQ] Mutation error', {
        mutationKey: mutation.options?.mutationKey,
        message: error?.message,
        stack: error?.stack,
      });
    },
    onSuccess: (data, variables, _context, mutation) => {
      logger.info('[RQ] Mutation success', {
        mutationKey: mutation.options?.mutationKey,
        variablesPreview: safePreview(variables),
        dataPreview: safePreview(data),
      });
    },
  }),
});

const unregisterDevServiceWorkers = async () => {
  if (!('serviceWorker' in navigator)) return;
  const regs = await navigator.serviceWorker.getRegistrations();
  await Promise.all(regs.map((r) => r.unregister()));
  if (typeof caches !== 'undefined' && caches.keys) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
  logger.info('[PWA] Dev mode: service workers + caches cleared');
};

// PWA service worker:
// - Prod: handled by `usePWA()` (so update UX + settings can coordinate reloads)
// - Dev: aggressively unregister to prevent "white screen" from stale cached chunks
if (!import.meta.env.PROD) {
  unregisterDevServiceWorkers().catch(() => {});
}

const ThemedApp = () => {
  const { mode } = useThemeMode();
  const theme = React.useMemo(() => createAppTheme(mode), [mode]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <PWAProvider>
          <SearchProvider>
            <DashboardProvider>
              <GoogleDomainAuthProvider>
                <GoogleDomainAuthGate>
                  <AppErrorBoundary>
                    <BulkSendStagingProvider>
                    <App />
                    </BulkSendStagingProvider>
                  </AppErrorBoundary>
                </GoogleDomainAuthGate>
              </GoogleDomainAuthProvider>
            </DashboardProvider>
          </SearchProvider>
        </PWAProvider>
      </Router>
    </ThemeProvider>
  );
};

const rootElement = document.getElementById('root');
const root = window.__GMER_REACT_ROOT__ || ReactDOM.createRoot(rootElement);
window.__GMER_REACT_ROOT__ = root;

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeModeProvider>
        <ThemedApp />
      </ThemeModeProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
